<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { getBackendErrorCode, handleAxiosError } from '@/composables/useAxiosError'
import { intervalKey, uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import type { PaymentInterval } from '@/types/purchase/PaymentInterval'
import { STATUS_CODES } from '@/constants'
import { berlinDay, termSwitchError } from '@/utils/termSwitch'
import BillingPeriodDialog from './BillingPeriodDialog.vue'

// The billing period of a running plan, its renewal date and a period change
// scheduled for the next renewal. Times are Unix seconds.
const props = defineProps<{
  subscriptionId: number | null | undefined
  billingFreq: PaymentInterval | null | undefined
  renewsAt?: number | null
  billingPeriods?: PaymentInterval[] | null
  pendingBillingFreq?: PaymentInterval | null
  pendingFrom?: number | null
  // A monthly vServer bills by the hour and has no renewal date.
  hourly?: boolean
  // A vServer, whose monthly option is billed by the hour.
  hourlyMonthly?: boolean
}>()

const emit = defineEmits<{ (e: 'changed'): void }>()

const { t } = useI18n()
const notifyStore = useNotifyStore()
const dialogOpen = ref(false)
const withdrawing = ref(false)

const periodName = (freq: PaymentInterval) => t(`common.billingPeriod.periods.${intervalKey(freq)}`)

const scheduled = computed(() =>
  props.pendingBillingFreq && props.pendingFrom
    ? { period: periodName(props.pendingBillingFreq), date: berlinDay(props.pendingFrom) }
    : null
)

const canChange = computed(
  () =>
    !!props.subscriptionId &&
    !scheduled.value &&
    (props.billingPeriods ?? []).some((p) => p !== 'free' && p !== props.billingFreq)
)

async function withdraw() {
  if (!props.subscriptionId) return
  withdrawing.value = true
  try {
    await useAxios('DELETE', `/subscription/${props.subscriptionId}/billing-period`, true)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('common.billingPeriod.label'),
      message: t('common.billingPeriod.withdrawn')
    })
    emit('changed')
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'BillingPeriodRow:withdraw', silentStatuses: [STATUS_CODES.CONFLICT] })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.billingPeriod.label'),
      message: termSwitchError(err, t)
    })
    // The row is stale: the change took effect or is gone already.
    const code = getBackendErrorCode(err)
    if (code === 'change_in_effect' || code === 'no_change_scheduled') emit('changed')
  } finally {
    withdrawing.value = false
  }
}
</script>

<template>
  <div v-if="billingFreq && billingFreq !== 'free'" class="billing-period-row" data-test="billing-period-row">
    <div class="period-main">
      <span class="period-label">
        <v-icon icon="mdi-calendar-sync-outline" size="16" />
        {{ $t('common.billingPeriod.label') }}
      </span>
      <span class="period-value" data-test="billing-period-current">{{ periodName(billingFreq) }}</span>
    </div>
    <p v-if="hourly" class="period-note" data-test="billing-period-hourly">{{ $t('common.billingPeriod.hourly') }}</p>
    <p v-else-if="renewsAt" class="period-note" data-test="billing-period-renews">
      {{ $t('common.billingPeriod.renewsOn', { date: berlinDay(renewsAt) }) }}
    </p>
    <div v-if="scheduled" class="period-scheduled" data-test="billing-period-scheduled">
      <span>{{ $t('common.billingPeriod.scheduled', scheduled) }}</span>
      <button
        v-if="subscriptionId"
        type="button"
        class="period-btn"
        data-test="billing-period-withdraw"
        :disabled="withdrawing"
        @click="withdraw"
      >
        <v-icon icon="mdi-undo-variant" size="16" />
        {{ $t('common.billingPeriod.withdraw') }}
      </button>
    </div>
    <button v-if="canChange" type="button" class="period-btn" data-test="billing-period-change" @click="dialogOpen = true">
      <v-icon icon="mdi-calendar-edit" size="16" />
      {{ $t('common.billingPeriod.change') }}
    </button>

    <BillingPeriodDialog
      v-if="subscriptionId && dialogOpen"
      v-model="dialogOpen"
      :subscription-id="subscriptionId"
      :hourly-monthly="hourlyMonthly"
      @changed="emit('changed')"
    />
  </div>
</template>

<style lang="scss" scoped>
.billing-period-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.period-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.period-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.period-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.period-note {
  margin: 0;
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.6);
}

.period-scheduled {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  background: rgba(0, 176, 255, 0.05);
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 10px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.85);
}

.period-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
